import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import {
  Button, Icon, Layout, OverlayView, ProgressStepper, Stack, StatusBar, Text, View,
} from '@piwikpro/ui-components';
import { FormValues } from '../types';
import { MOBILE_APP_STEPS, WEBSITE_APP_STEPS } from '../steps';

interface AppCreatorFormContentProps {
  api: FormikProps<FormValues>
  progressStepperStep: number
  isWebsiteApp: boolean
  prefilled: boolean
  isCreatingApp: boolean
  isConsentManagerStepReady: boolean
  children: React.ReactNode
  handleProgressStepperStepChange: (step: number, handleSubmit?: () => void) => Promise<void>
  getPrimaryButtonTextKey: () => string
  UnsavedChangesModal: React.ElementType
  onClose: () => void
}

const FormOverlay = ({
  api,
  progressStepperStep,
  isWebsiteApp,
  prefilled,
  isCreatingApp,
  isConsentManagerStepReady,
  children,
  handleProgressStepperStepChange,
  getPrimaryButtonTextKey,
  UnsavedChangesModal,
  onClose,
}: AppCreatorFormContentProps) => {
  const { t } = useTranslation(['administration', 'platform']);
  const isBasicConfigurationStep = progressStepperStep === (isWebsiteApp ? WEBSITE_APP_STEPS.BASIC_CONFIGURATION : MOBILE_APP_STEPS.BASIC_CONFIGURATION);
  const progressStepper = isWebsiteApp ? (
    <ProgressStepper current={progressStepperStep}>
      <ProgressStepper.Step status={progressStepperStep > WEBSITE_APP_STEPS.BASIC_CONFIGURATION ? 'finished' : 'current'} title={t('apps.creator.basicConfigurationStep')} />
      <ProgressStepper.Step status={progressStepperStep > WEBSITE_APP_STEPS.CONSENT_MANAGER ? 'finished' : 'current'} title={t('apps.creator.privacyAndConsentManagementToolStep')} />
      <ProgressStepper.Step status={progressStepperStep > WEBSITE_APP_STEPS.INSTALLATION ? 'finished' : 'current'} title={t('apps.creator.installationStep')} />
    </ProgressStepper>
  ) : (
    <ProgressStepper current={progressStepperStep}>
      <ProgressStepper.Step status={progressStepperStep > MOBILE_APP_STEPS.BASIC_CONFIGURATION ? 'finished' : 'current'} title={t('apps.creator.basicConfigurationStep')} />
      <ProgressStepper.Step status={progressStepperStep > MOBILE_APP_STEPS.INSTALLATION ? 'finished' : 'current'} title={t('apps.creator.installationStep')} />
    </ProgressStepper>
  );

  return (
    <OverlayView
      appearAnimation={!prefilled}
      isOpen
      onClose={onClose}
      title={!prefilled
        ? t('apps.creator.title')
        : (
          <Stack verticalAlignment="center">
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.editTitle')}
              </Text>
            </Stack.Item>
            <Stack.Item wrap>
              <Stack spacing="extraNarrow" verticalAlignment="center">
                <Stack.Item>
                  <Icon name="info-circle" color="blue" size="small" />
                </Stack.Item>
                <Stack.Item fill>
                  <Text as="p" size={12} dataId="Caption" color="InkLighter">
                    {t('apps.creator.editSubtitle')}
                  </Text>
                </Stack.Item>
              </Stack>
            </Stack.Item>
          </Stack>
        )}
    >
      <View
        heightAuto
        overflowTopElement={(<StatusBar type="default">{progressStepper}</StatusBar>)}
      >
        <View.Body>
          {children}
        </View.Body>
        <View.Action isFloating>
          <Layout maxWidth="medium">
            <Stack horizontalAlignment="right" spacing="narrow">
              {!isBasicConfigurationStep && (
                <Stack.Item>
                  <Button
                    text={t('apps.creator.previousButton')}
                    onClick={() => handleProgressStepperStepChange(progressStepperStep - 1)}
                  />
                </Stack.Item>
              )}
              <Stack.Item fill />
              <Stack.Item>
                <UnsavedChangesModal />
                <Button text={t('components:cancel')} onClick={onClose} />
              </Stack.Item>
              <Stack.Item>
                <Button
                  disabled={(isBasicConfigurationStep && !api.isValid) || (isWebsiteApp && progressStepperStep === WEBSITE_APP_STEPS.CONSENT_MANAGER && !isConsentManagerStepReady)}
                  pending={isCreatingApp}
                  text={t(getPrimaryButtonTextKey())}
                  appearance="primary"
                  onClick={() => handleProgressStepperStepChange(progressStepperStep + 1, api.handleSubmit)}
                />
              </Stack.Item>
            </Stack>
          </Layout>
        </View.Action>
      </View>
    </OverlayView>
  );
};

export default FormOverlay;
